import * as R from 'ramda';

import { isValueExist } from '@/utils/utils';
import { AUDIENCE_ROWS } from '@/utils/dashboard';

const ERROR_MAP = {
    NOT_AUDIENCE_TIME_TOGETHER: 'NOT_AUDIENCE_TIME_TOGETHER',
    DUPLICATED_TIME_BREAKDOWN: 'DUPLICATED_TIME_BREAKDOWN',
    DETAIL_NO_CHILDREN: 'DETAIL_NO_CHILDREN',
    INVALID_COMBINATION: 'INVALID_DIMENSIONS_METRICS_COMBINATION',
    AUDIENCE_BEFORE_CAMPAIGN_STRUCTURE: 'AUDIENCE_BEFORE_CAMPAIGN_STRUCTURE',
    SUBCHANNEL_BEFORE_MARKETING_LEVEL: 'SUBCHANNEL_BEFORE_MARKETING_LEVEL',
};

const CAMPAIGN_STRUCTURE_ROWS = [
    'channel',
    'campaign',
    'adSet',
    'ad',
    'keyword',
];

const MARKETING_LEVEL_ROWS = ['campaign', 'adSet', 'ad'];

/**
 * BE의 parent→child 필터 전파 수정이 반영된 채널.
 * 선택된 채널이 전부 이 목록에 속하면 순서 규칙
 * (AUDIENCE_BEFORE_CAMPAIGN_STRUCTURE, SUBCHANNEL_BEFORE_MARKETING_LEVEL)을 적용하지 않는다.
 */
const ORDER_RULE_EXEMPT_CHANNELS = ['google'];

const isOrderRuleExempt = (selectedChannels) =>
    isValueExist(selectedChannels) &&
    selectedChannels.every((channel) =>
        ORDER_RULE_EXEMPT_CHANNELS.includes(channel)
    );

const checkForbiddenCombo = (forbiddenList = []) =>
    R.compose(R.lte(2), R.length, R.intersection(forbiddenList));
const AD_SEARCH_TERM_FORBIDDEN_LIST = [
    'ad',
    'search_term_view',
    'search_term_match_type',
    'search_term',
];
const AD_ID_SEARCH_TERM_FORBIDDEN_LIST = [
    'adId',
    'search_term_view',
    'search_term_match_type',
    'search_term',
];
const checkForbiddenAdSearchTermCombo = checkForbiddenCombo(
    AD_SEARCH_TERM_FORBIDDEN_LIST
);
const checkForbiddenAdIdSearchTermCombo = checkForbiddenCombo(
    AD_ID_SEARCH_TERM_FORBIDDEN_LIST
);

export const checkOrderRule = function ({ list, changeOrder, selectedChannels }) {
    const {
        INVALID_COMBINATION,
        AUDIENCE_BEFORE_CAMPAIGN_STRUCTURE,
        SUBCHANNEL_BEFORE_MARKETING_LEVEL,
    } = ERROR_MAP;
    let type;

    const skipOrderRules = isOrderRuleExempt(selectedChannels);

    const hasOverOneAudiences =
        list.filter((name) => AUDIENCE_ROWS.includes(name)).length > 1;
    const hasKeywordAndAd =
        R.any(
            (v) => ['keywordText', 'keyword', 'keywordId'].includes(v),
            list
        ) && R.any((v) => ['adId', 'ad'].includes(v), list);
    const hasForbiddenAdSearchTermCombo = checkForbiddenAdSearchTermCombo(list);
    const hasForbiddenAdIdSearchTermCombo =
        checkForbiddenAdIdSearchTermCombo(list);

    // Check if campaign structure breakdowns come after audience breakdowns
    const hasAudienceBeforeCampaignStructure = (() => {
        if (skipOrderRules) return false;
        const audienceIndices = list
            .map((name, index) => (AUDIENCE_ROWS.includes(name) ? index : -1))
            .filter((index) => index !== -1);

        const campaignStructureIndices = list
            .map((name, index) =>
                CAMPAIGN_STRUCTURE_ROWS.includes(name) ? index : -1
            )
            .filter((index) => index !== -1);

        if (
            !isValueExist(audienceIndices) ||
            !isValueExist(campaignStructureIndices)
        ) {
            return false;
        }

        // Check if any campaign structure comes after any audience
        return campaignStructureIndices.some((csIndex) =>
            audienceIndices.some((audIndex) => csIndex > audIndex)
        );
    })();

    // Check if subChannel comes before marketing level breakdowns
    const hasSubChannelBeforeMarketingLevel = (() => {
        if (skipOrderRules) return false;
        const subChannelIndex = list.indexOf('subChannel');
        if (subChannelIndex === -1) return false;

        const marketingLevelIndices = list
            .map((name, index) =>
                MARKETING_LEVEL_ROWS.includes(name) ? index : -1
            )
            .filter((index) => index !== -1);

        if (!isValueExist(marketingLevelIndices)) return false;

        // Check if subChannel comes before any marketing level
        return marketingLevelIndices.some(
            (mlIndex) => subChannelIndex < mlIndex
        );
    })();

    const hasGeneralInvalidCombo =
        hasOverOneAudiences ||
        hasKeywordAndAd ||
        hasForbiddenAdSearchTermCombo ||
        hasForbiddenAdIdSearchTermCombo;

    if (hasAudienceBeforeCampaignStructure) {
        type = AUDIENCE_BEFORE_CAMPAIGN_STRUCTURE;
    } else if (hasSubChannelBeforeMarketingLevel) {
        type = SUBCHANNEL_BEFORE_MARKETING_LEVEL;
    } else if (!changeOrder && hasGeneralInvalidCombo) {
        type = INVALID_COMBINATION;
    }

    return { available: !type, type };
};
